import React, { useState } from 'react';
import { apiService } from '../../services/api';
import { useMutation } from '../../hooks/useApi';
import logger from '../../utils/logger';
import { useSettings } from '../../context/SettingsContext';
import {
  FileDown,
  Eye,
  ChevronsLeft,
  ChevronLeft,
  ChevronRight,
  ChevronsRight
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

function RaportShitjePerShites() {
  const { getCurrencyCode } = useSettings();
  const currencyCode = getCurrencyCode();
  const { roli } = useAuth();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [shitjet, setShitjet] = useState({});
  const [error, setError] = useState('');
  const [faqjaAktuale, setFaqjaAktuale] = useState(1);
  const perFaqe = 5;

  const { mutate: fetchRaport, loading: loadingRaport } = useMutation(
    async ({ from, to }) => {
      return await apiService.get('/raport-shitje-shites', { params: { from, to } });
    },
    {
      onSuccess: (data) => {
        setShitjet(data);
        setFaqjaAktuale(1);
        setError('');
      },
      onError: () => {
        setError('Nuk mund të merret raporti për shitësit.');
      },
      showNotifications: false
    }
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFaqjaAktuale(1);
    fetchRaport({ from, to });
  };

  const handleExportPdf = async () => {
    if (!from || !to) {
      setError('Ju lutem zgjidhni datat për PDF.');
      return;
    }
    try {
      const response = await apiService.get('/raport-shitje-shites/pdf', {
        params: { from, to },
        responseType: 'blob'
      });
      const file = new Blob([response], { type: 'application/pdf' });
      window.open(URL.createObjectURL(file));
    } catch (err) {
      logger.error('PDF export error:', err);
      setError('Dështoi shkarkimi i PDF-it.');
    }
  };

  const renderTabela = (lista, lokacioni) => {
    const offset = (faqjaAktuale - 1) * perFaqe;
    const faqja = lista.slice(offset, offset + perFaqe);
    const faqetTotale = Math.ceil(lista.length / perFaqe);
    const totaliGjithsej = lista.reduce((sum, s) => sum + Number(s.totali), 0);

    return (
      <div key={lokacioni} className="space-y-6">
        <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300">Lokacioni: {lokacioni}</h3>

        {/* Pagination */}
        <div className="flex justify-between items-center text-sm text-gray-600 dark:text-gray-400">
          <span>Faqe {faqjaAktuale} nga {faqetTotale}</span>
          <div className="flex gap-1">
            <button onClick={() => setFaqjaAktuale(1)} disabled={faqjaAktuale===1} className="btn-premium-secondary disabled:opacity-50 disabled:cursor-not-allowed"><ChevronsLeft size={16}/></button>
            <button onClick={() => setFaqjaAktuale(faqjaAktuale-1)} disabled={faqjaAktuale===1} className="btn-premium-secondary disabled:opacity-50 disabled:cursor-not-allowed"><ChevronLeft size={16}/></button>
            <button onClick={() => setFaqjaAktuale(faqjaAktuale+1)} disabled={faqjaAktuale===faqetTotale} className="btn-premium-secondary disabled:opacity-50 disabled:cursor-not-allowed"><ChevronRight size={16}/></button>
            <button onClick={() => setFaqjaAktuale(faqetTotale)} disabled={faqjaAktuale===faqetTotale} className="btn-premium-secondary disabled:opacity-50 disabled:cursor-not-allowed"><ChevronsRight size={16}/></button>
          </div>
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto rounded-xl border border-gray-200 dark:border-dark-700 shadow">
          <table className="table-premium">
            <thead>
              <tr>
                <th className="p-3 text-left">#</th>
                <th className="p-3 text-left">Shitësi</th>
                <th className="p-3 text-right">Nr. Shitjeve</th>
                <th className="p-3 text-right">Totali ({currencyCode})</th>
              </tr>
            </thead>
            <tbody>
              {faqja.map((s, i) => (
                <tr key={i} className="border-t border-gray-200 dark:border-dark-700 hover:bg-gray-50 dark:hover:bg-dark-700/50">
                  <td className="p-3 text-gray-700 dark:text-gray-300">{offset + i + 1}</td>
                  <td className="p-3 font-medium text-gray-700 dark:text-gray-300">{s.shitesi}</td>
                  <td className="p-3 text-right text-primary-600 dark:text-primary-400 font-semibold">{s.numri_shitjeve}</td>
                  <td className="p-3 text-right font-semibold text-green-600 dark:text-green-400">{Number(s.totali).toFixed(2)} Den</td>
                </tr>
              ))}
              <tr className="bg-gray-50 dark:bg-dark-700 font-bold border-t border-gray-200 dark:border-dark-700">
                <td colSpan="3" className="p-3 text-right text-gray-800 dark:text-gray-100">Totali i Lokacionit:</td>
                <td className="p-3 text-right text-green-600 dark:text-green-400">{totaliGjithsej.toFixed(2)} Den</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden space-y-4">
          {faqja.map((s, i) => (
            <div key={i} className="card-premium p-4 space-y-1">
              <div className="text-gray-700 dark:text-gray-300"><strong>#:</strong> {offset + i + 1}</div>
              <div className="text-gray-700 dark:text-gray-300"><strong>Shitësi:</strong> {s.shitesi}</div>
              <div className="text-gray-700 dark:text-gray-300"><strong># Shitjeve:</strong> <span className="font-semibold text-primary-600 dark:text-primary-400">{s.numri_shitjeve}</span></div>
              <div className="text-green-600 dark:text-green-400 font-semibold"><strong>Totali:</strong> {Number(s.totali).toFixed(2)} Den</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <h2 className="text-3xl md:text-4xl font-bold mb-8 text-gradient-logo flex items-center gap-3 animate-slide-down">
        <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
          🧾
        </div>
        Raporti i Shitjeve për Shitës
      </h2>

      <form onSubmit={handleSubmit} className="card-premium p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="form-label">Nga data</label>
          <input
            type="date" value={from}
            onChange={e => setFrom(e.target.value)}
            className="input-premium"
            required
          />
        </div>
        <div>
          <label className="form-label">Deri më</label>
          <input
            type="date" value={to}
            onChange={e => setTo(e.target.value)}
            className="input-premium"
            required
          />
        </div>
        <button
          type="submit"
          className="btn-primary mt-auto"
        >
          <Eye size={18} /> Shfaq
        </button>
      </form>

      {error && <p className="form-error text-center">{error}</p>}

      {loadingRaport && (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent"></div>
        </div>
      )}

      {Object.keys(shitjet).length > 0 && (
        <div className="space-y-10">
          <button onClick={handleExportPdf} className="btn-primary">
            <FileDown size={18} /> Shkarko PDF
          </button>

          {Object.entries(shitjet).map(([lokacioni, lista]) =>
            renderTabela(lista, lokacioni)
          )}
        </div>
      )}

      {!loadingRaport && Object.keys(shitjet).length === 0 && !error && (
        <div className="empty-state">
          <p className="empty-state-title">Asnjë e dhënë për t'u shfaqur</p>
          <p className="empty-state-description">Zgjidhni datat dhe klikoni "Shfaq" për të parë raportin.</p>
        </div>
      )}
    </div>
  );
}

export default RaportShitjePerShites;
